import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { I, toolIcon } from './Icons';
import { reduced } from './useHomeData';

export const COMPLETION_TYPES = ['portal', 'esign', 'idv'];
export const isDone = (q) => COMPLETION_TYPES.includes(q.type);
const DONE_LABEL = { portal: 'Client portal completed', esign: 'E-sign completed', idv: 'ID verification completed' };

export const FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'ready', label: 'Completed by client', test: (q) => isDone(q) },
  { id: 'follow', label: 'Follow-ups', test: (q) => q.type === 'follow' },
  { id: 'task', label: 'My tasks', test: (q) => q.type === 'task' },
  { id: 'jarvis', label: 'Jarvis flagged', test: (q) => q.type === 'jarvis' || !!q.jarvis },
];
const tagClass = (t) => (t === 'esign' || t === 'jarvis' ? 'tag-violet' : t === 'follow' ? 'tag-amber' : t === 'task' ? 'tag-violet' : 'tag-green');

export const confetti = (x, y) => {
  if (reduced()) return;
  const colours = ['#2347FF', '#3CC8FF', '#2FD07F', '#90D5FF', '#7C4DFF', '#FFB547'];
  const host = document.querySelector('.dwh') || document.body;
  for (let i = 0; i < 26; i += 1) {
    const c = document.createElement('i'); c.className = 'confetti';
    c.style.left = `${x}px`; c.style.top = `${y}px`; c.style.background = colours[i % colours.length];
    c.style.setProperty('--dx', `${Math.random() * 260 - 130}px`); c.style.setProperty('--dy', `${Math.random() * -200 - 40}px`); c.style.setProperty('--r', `${Math.random() * 720 - 360}deg`);
    host.appendChild(c); setTimeout(() => c.remove(), 1500);
  }
};

// One queue row. Client completions pulse (`attn`) until acknowledged, then stay highlighted (`acked`) until actioned.
const Row = ({ q, i, sel, leaving, onSelect, onDone, onSnooze, onDraft, onAck, onOpen, onJarvis, first, newId }) => {
  const done = isDone(q); const attn = done && !q.acknowledged; const jv = q.type === 'jarvis';
  return (
    <li className={`item${sel ? ' sel' : ''}${q.id === newId ? ' new' : ''}${attn ? ' attn' : ''}${done && !attn ? ' acked' : ''}${leaving === 'zap' ? ' zap' : ''}${leaving === 'out' || leaving === 'slide' ? ' out' : ''}`}
      data-id={q.id} style={{ animationDelay: `${(first ? 0.55 : 0) + i * 0.06}s` }} onClick={onSelect} data-testid={`queue-item-${i}`} data-attention={attn ? 'true' : 'false'}>
      <span className="zapline" />{attn && <span className="attn-shine" />}
      <div className={`ini t-${q.tint}`}>{q.initials}<span className="ok"><I id="check" /></span></div>
      <div style={{ minWidth: 0 }}>
        <div className="item-top">
          <span className="item-name">{q.name}</span>
          {done ? <span className="ready" data-testid={`queue-completed-badge-${i}`}><i />{DONE_LABEL[q.type]}</span> : <span className={`tag ${tagClass(q.type)}`}>{q.tag}</span>}
          {attn && <span className="new-pill" data-testid={`queue-new-${i}`}>New</span>}
          {!done && <span className={`why${q.hot ? ' hot' : ''}`}>{q.hot && <span className="ld amber" />}{q.why}</span>}
        </div>
        {done
          ? <div className="item-meta"><span className="done-time" data-testid={`queue-completed-time-${i}`}><I id="clock" size={12} />{q.completed_label}</span>{q.meta ? ` · ${q.meta.split(' · ')[0]}` : ''}</div>
          : <div className="item-meta">{q.meta}</div>}
        <div className="next"><I id="spark" /><span>{jv && <b>Jarvis: </b>}{q.next}</span></div>
        {q.jarvis && <div className="next" data-testid={`queue-jarvis-note-${i}`}><I id="spark" /><span><b>Jarvis: </b>{q.jarvis}</span></div>}
      </div>
      <div className="item-actions">
        {attn && <button type="button" className="btn btn-ghost btn-sm" title="Mark as reviewed" onClick={(e) => { e.stopPropagation(); onAck(); }} data-testid={`queue-ack-${i}`}><I id="userok" size={15} />Got it</button>}
        {!done && <button type="button" className="icon-btn" aria-label={`Snooze ${q.name}`} title="Snooze (S)" onClick={(e) => { e.stopPropagation(); onSnooze(); }} data-testid={`queue-snooze-${i}`}><I id="clock" size={17} /></button>}
        {!jv && <button type="button" className="btn btn-ghost btn-sm" title="Open draft (D)" onClick={(e) => { e.stopPropagation(); onDraft(); }} data-testid={`queue-draft-${i}`}><I id="pen" size={15} />Draft</button>}
        {jv ? <button type="button" className="btn btn-solid btn-sm" title="Review what Jarvis found" onClick={(e) => { e.stopPropagation(); onJarvis(); }} data-testid={`queue-jarvis-review-${i}`}><I id="spark" size={15} />Review</button> : done
          ? <button type="button" className="btn btn-solid btn-sm" title="Open the client's file" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid={`queue-open-${i}`}><I id="arrow" size={15} />Open File</button>
          : <button type="button" className="btn btn-solid btn-sm" title="Done (E)" onClick={(e) => { e.stopPropagation(); onDone(); }} data-testid={`queue-done-${i}`}><I id={q.type === 'follow' ? 'phone' : 'check'} size={15} />{q.primary}</button>}
      </div>
    </li>
  );
};

export const ActionQueue = ({ queue, filter, setFilter, sel, setSel, onDone, onSnooze, onDraft, onAck, onAddTask, newId, onMore, onJarvis }) => {
  const list = queue.items.filter(FILTERS.find((f) => f.id === filter).test);
  const [leaving, setLeaving] = useState({});
  const firstRender = useRef(true);
  useEffect(() => { const t = setTimeout(() => { firstRender.current = false; }, 1500); return () => clearTimeout(t); }, []);
  const total = queue.total_today || 0; const cleared = queue.cleared_today || 0;
  const empty = queue.items.length === 0;
  const navigate = useNavigate();
  const nDone = list.filter(isDone).length; const nAttn = list.filter((q) => isDone(q) && !q.acknowledged).length;
  const zap = (q, mode, fn) => {
    setLeaving((l) => ({ ...l, [q.id]: mode }));
    if (mode === 'zap') setTimeout(() => setLeaving((l) => ({ ...l, [q.id]: 'out' })), 420);
    setTimeout(() => { setLeaving((l) => { const n = { ...l }; delete n[q.id]; return n; }); fn(); }, mode === 'zap' ? 800 : 380);
  };
  const done = (q) => zap(q, 'zap', () => { onDone(q); if (queue.items.length === 1) { const r = document.getElementById('queue')?.getBoundingClientRect(); if (r) confetti(r.left + r.width / 2, r.top + 120); } });
  const snooze = (q) => zap(q, 'slide', () => onSnooze(q));
  return (
    <section className="card card-pad rv" style={{ animationDelay: '.42s' }} id="queue" aria-labelledby="qTitle" data-testid="action-queue">
      <div className="card-head">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div className="ai-mark"><I id="spark" /></div>
          <div>
            <div className="eyebrow" style={{ fontSize: 10 }}>Action queue</div>
            <h2 id="qTitle" style={{ marginTop: 4 }}>What needs you next</h2>
            <div className="card-sub">Client completions always come first. Every next step is drafted and ready.</div>
          </div>
        </div>
        <button type="button" className="btn btn-solid btn-sm" onClick={onAddTask} data-testid="queue-add-task"><I id="plus" size={14} sw={2.2} />Add task</button>
      </div>
      <div className="q-progress"><div className="track"><i style={{ width: `${total ? (cleared / total) * 100 : 0}%` }} /></div><span data-testid="queue-count"><b>{cleared}</b> / {total} cleared today</span></div>
      {!empty && (
        <div className="filters" role="group" aria-label="Filter queue" data-testid="queue-filters">
          {FILTERS.map((f) => { const n = queue.items.filter(f.test).length; return (n > 0 || f.id === 'all') && <button type="button" key={f.id} className="filter" aria-pressed={filter === f.id} onClick={() => { setFilter(f.id); setSel(0); }} data-testid={`queue-filter-${f.id}`}>{f.label}<span className="n">{n}</span></button>; })}
        </div>
      )}
      {!empty && (
        <ul className="queue" data-testid="queue-list">
          {list.map((q, i) => (
            <React.Fragment key={q.id}>
              {i === 0 && nDone > 0 && <li className="q-group done" data-testid="queue-group-completed"><span className="ld" />Completed by client<span className="hide-sm"> · action now</span>{nAttn > 0 && <span className="q-attn" data-testid="queue-unreviewed-count">{nAttn} new</span>}<span className="n">{nDone}</span></li>}
              {i === nDone && nDone > 0 && <li className="q-group" aria-hidden="true">Everything else<span className="n">{list.length - nDone}</span></li>}
              <Row q={q} i={i} sel={i === sel} leaving={leaving[q.id]} first={firstRender.current} newId={newId} onSelect={() => { setSel(i); if (isDone(q)) onAck(q); }} onDone={() => done(q)} onSnooze={() => snooze(q)} onDraft={() => onDraft(q)} onAck={() => onAck(q)} onOpen={() => { onAck(q); navigate(q.to || '/onboarding'); }} onJarvis={() => onJarvis(q.insight)} />
            </React.Fragment>
          ))}
        </ul>
      )}
      {!empty && queue.more > 0 && filter === 'all' && <a className="more-link" href="/lead-nurture" onClick={(e) => { e.preventDefault(); onMore(); }} data-testid="queue-more">+{queue.more} more in Lead Nurture<I id="arrow" size={14} style={{ marginLeft: 6 }} /></a>}
      <div className={`empty${empty ? ' show' : ''}`} data-testid="queue-empty">
        <div className="empty-badge"><I id="check" /></div>
        <h3>Queue clear. Nice work.</h3>
        <p>Nothing is waiting on you. Jarvis will flag anything new the moment it needs you.</p>
      </div>
      <div className="hint hide-sm">
        <span><kbd>J</kbd><kbd>K</kbd> move</span><span><kbd>E</kbd> done</span><span><kbd>D</kbd> draft</span><span><kbd>S</kbd> snooze</span><span><kbd>?</kbd> all shortcuts</span>
      </div>
    </section>
  );
};

export const PROMPTS = [
  { q: 'Who should I call first?', ic: 'phone' },
  { q: "What's waiting on the team?", ic: 'userok' },
  { q: 'Summarise my week', ic: 'chart' },
];

export const RightColumn = ({ favTools, onLaunch, onAsk, onCustomise }) => (
  <div className="stack">
    <section className="rv" style={{ animationDelay: '.58s' }} aria-labelledby="qlTitle" data-testid="quick-launch">
      <div className="sec-label"><h2 id="qlTitle">Quick launch</h2><a href="#customise" onClick={(e) => { e.preventDefault(); onCustomise(); }} data-testid="quick-launch-customise">Customise</a></div>
      <div className="tiles">
        {favTools.slice(0, 4).map((t, i) => (
          <a key={t.key} className={`tile${i === 0 ? ' hero' : ''}`} href={t.path} onClick={(e) => { e.preventDefault(); onLaunch(t); }} data-testid={`tile-${t.key}`}>
            <span className="ti"><I id={toolIcon(t)} /></span><I id="upr" size={16} className="go" /><span>{t.title}</span>
          </a>
        ))}
        {!favTools.length && <button type="button" className="tile" onClick={onCustomise}><span className="ti"><I id="plus" /></span><span>Pick your favourites</span></button>}
      </div>
    </section>
    <div className="foot-note rv" style={{ animationDelay: '.66s' }}><span className="ld" />Live from Game Plan Tracker, Client Portals and Lead Nurture.</div>
  </div>
);
